import { useContext } from 'react';
import { Container } from 'react-modal-promise';
import { useNavigate } from 'react-router-dom';
import { MapModeContext } from 'app/agriculture/contexts/MapModeContextProvider';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { WeatherButton } from 'app/agriculture/FarmMain/TopBar/components/WeatherButton/WeatherButton';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { MainNav } from 'app/agriculture/FarmMain/TopBar/components/MainNav/MainNav';
import { AddNewButton } from 'app/agriculture/FarmMain/TopBar/components/AddNewButton/AddNewButton';
import { SecondaryNav } from 'app/agriculture/FarmMain/TopBar/components/SecondaryNav/SecondaryNav';
import { HouseIcon } from 'app/agriculture/icons';
import { FarmContext } from 'app/agriculture/contexts/RequiredStartupProviders/FarmContext/FarmContextProvider';
import { logout } from 'app/agriculture/FarmMain/TopBar/logout';
import './TopBar.scss';
import { AgricultureMainBanner } from 'app/agriculture/FarmMain/AgricultureMainBanner';
import { useFarmPath } from 'app/agriculture/router/useFarmPath';
import { useCurrentUser } from 'app/agriculture/hooks/users/useCurrentUser';
import { ProfileAvatar } from 'app/agriculture/infobox/common/ProfileAvatar';
import { DeviceIcon } from 'app/agriculture/icons/devices/DeviceIcon';

export function TopBar() {
    const routeActions = useRouteActions();
    const farmPath = useFarmPath();
    const navigate = useNavigate();
    const { name } = useCurrentUser();
    const { mapMode, setMapMode } = useContext(MapModeContext);

    return (
        <div className="TopBar">
            <div className="TopBar__main">
                <MainNav
                    activeTab={mapMode}
                    items={[
                        {
                            id: 'open-overview-button',
                            text: 'Overview',
                            startIcon: <HouseIcon />,
                            onClick: () => {
                                navigate(farmPath('/'));
                                setMapMode('Overview');
                            },
                        },
                        {
                            id: 'open-notes-button',
                            text: 'Notes',
                            startIcon: 'note',
                            onClick: () => {
                                routeActions.openList('Note');
                                setMapMode('Notes');
                            },
                        },
                        {
                            id: 'open-tasks-button',
                            text: 'Tasks',
                            startIcon: 'task',
                            onClick: () => {
                                routeActions.openList('Task');
                                setMapMode('Tasks');
                            },
                        },
                        {
                            id: 'open-herds-button',
                            text: 'Herds',
                            startIcon: 'herd',
                            onClick: () => {
                                routeActions.openList('Herd');
                                setMapMode('Herds');
                            },
                        },
                        {
                            id: 'open-devices-button',
                            text: 'Devices',
                            startIcon: 'device',
                            onClick: () => {
                                setMapMode('Devices');
                                routeActions.openList('Device');
                            },
                            subMenuItems: [
                                {
                                    id: 'open-all-devices-button',
                                    text: 'All Devices',
                                    startIcon: 'device',
                                    onClick: () => {
                                        routeActions.openList('Device');
                                    },
                                },
                                {
                                    id: 'open-weather-stations-button',
                                    text: 'Weather Stations',
                                    startIcon: <DeviceIcon deviceType="Weather Station" />,
                                    onClick: () => {
                                        navigate(farmPath('/devices/weather-stations/'));
                                    },
                                },
                                {
                                    id: 'open-water-button',
                                    text: 'Water',
                                    startIcon: 'tank',
                                    onClick: () => {
                                        navigate(farmPath('/devices/level-monitors/'));
                                    },
                                },
                            ],
                        },
                    ]}
                />
                <div className="TopBar__side">
                    <AddNewButton className="TopBar__add-new-button" />
                    <SuspenseWrapper>
                        <WeatherButton />
                    </SuspenseWrapper>
                    <SecondaryNav
                        items={[
                            {
                                id: 'secondary-nav-settings-button',
                                icon: 'settings',
                                tooltip: 'Settings',
                                subNavItems: [
                                    {
                                        id: 'settings-sub-nav-settings-button',
                                        onClick: () => {
                                            navigate(`/farms${farmPath('/edit')}`);
                                        },
                                        startIcon: 'settings',
                                        text: 'Farm settings',
                                    },
                                    {
                                        id: 'settings-sub-nav-default-view-button',
                                        onClick: () => {
                                            navigate(farmPath('/default-view'));
                                        },
                                        startIcon: 'defaultView',
                                        text: 'Adjust starting map view',
                                    },
                                ],
                            },
                            {
                                id: 'secondary-nav-profile-button',
                                icon: 'profile',
                                tooltip: 'Profile',
                                subNavItems: [
                                    {
                                        id: 'profile-sub-nav-profile-button',
                                        onClick: () => {
                                            navigate('/profile');
                                        },
                                        startIcon: <ProfileAvatar name={name} />,
                                        text: name,
                                    },
                                    {
                                        id: 'profile-sub-nav-logout-button',
                                        onClick: () => {
                                            void logout();
                                        },
                                        startIcon: 'logout',
                                        text: 'Logout',
                                    },
                                ],
                            },
                        ]}
                    />
                </div>
            </div>
            <Container />
            <AgricultureMainBanner />
        </div>
    );
}
